// src/aiService.js
// OpenAI-powered AI evaluation for answers and resume.
// Falls back to keyword scoring if no API key is set.

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const OPENAI_API_URL = "https://api.openai.com/v1/chat/completions"; // Not used anymore, kept for reference

// ─── Keyword fallback for answers ───
const ANSWER_KEYWORDS = [
  // Technical & Specific
  "algorithm","data structure","database","api","framework","optimization",
  "performance","security","testing","deployment","architecture","design pattern",
  "scalability","debugging","agile","scrum","javascript","react","node","python",
  "java","sql","cloud","docker","linux","git","rest","graphql","frontend","backend",
  "fullstack","mobile","web","application","software","devops","ci/cd","aws","azure",
  "gcp","firebase","kubernetes","terraform","microservices",
  // Action Verbs & Soft Skills
  "implemented","developed","built","managed","collaborated","designed","analyzed",
  "resolved","leadership","teamwork","growth","learning","skill","expert","professional",
  "reliable","responsible","initiative","communication","collaboration","multithreading",
  "concurrency","deadlock","mutex","semaphore","lock","synchronization","thread",
  "resource","process","refactor","deployed","fixed","improved","led","mentored",
  "delivered","achieved","impacted","contributed","partnered","organized","prioritized",
  "problem-solve","innovative","quality","efficiency","deadline"
];

function keywordScoreAnswer(questionText, answerText, category = "") {
  if (!answerText || !answerText.trim()) return { score: 0, feedback: "No answer provided. Please provide a professional response." };
  
  const lowerAns = answerText.toLowerCase().trim();
  const lowerQue = questionText.toLowerCase().trim();
  const wordCount = lowerAns.split(/\s+/).filter(Boolean).length;
  
  // 🎙️ STEP 1: Participation Points
  // Every non-empty answer starts with a base score of 5.0 as per user request
  let baseScore = wordCount >= 1 ? 5.0 : 0;

  // 🎙️ STEP 2: Math/Logic Detection
  const mathMarkers = [
    "lcm", "calculate", "math", "find x", "sum", "arithmetic", "average", "mean", "median", "mode", 
    "percentage", "probability", "solve", "series", "sequence", "ratio", "proportion", "profit", 
    "interest", "work", "time", "speed", "distance", "value of", "how much", "simplify", "factor",
    "square root", "root", "cube", "power", "multiply", "divide", "add", "subtract", "minus", "plus",
    "what is the value", "determine", "evaluate", "simplify", "rationalize", "logarithm", "trigonometry"
  ];
  
  const isAptitudeCat = category.toLowerCase().includes("aptitude") || category.toLowerCase().includes("math");
  const hasMathKeywords = mathMarkers.some(m => lowerQue.includes(m));
  const hasMathSymbols = /[\+\-\*\/\^=]/.test(lowerQue);
  const containsNumbersInQue = /\d+/.test(lowerQue);
  
  const isMathQ = isAptitudeCat || hasMathKeywords || (hasMathSymbols && containsNumbersInQue);
  
  // More permissive numeric detection (matches "12", "15", "15m", "10%", "Rs. 500", etc.)
  const containsNumber = /\d+/.test(lowerAns);
  const isShortNumeric = lowerAns.length < 15 && containsNumber;

  if (isMathQ && isShortNumeric) {
    // 💡 NEW: Quick Local Verification for common math answers
    const numbersInQue = lowerQue.match(/\d+/g) || [];
    const answerNum = lowerAns.match(/\d+/);
    
    // Example: "square root of 144" -> [144], ans -> 12. 12*12=144.
    if (lowerQue.includes("square root") && numbersInQue.length === 1 && answerNum) {
      const root = parseInt(answerNum[0]);
      if (root * root === parseInt(numbersInQue[0])) {
        return { score: 10.0, feedback: "Correct numeric answer (verified locally). Excellent!" };
      }
    }
    
    // Example: "15 + 10" -> [15, 10], ans -> 25.
    if (lowerQue.includes("+") && numbersInQue.length === 2 && answerNum) {
      if (parseInt(numbersInQue[0]) + parseInt(numbersInQue[1]) === parseInt(answerNum[0])) {
        return { score: 10.0, feedback: "Correct addition (verified locally). Excellent!" };
      }
    }

    // If we can't verify it locally, we give the base score
    return { 
      score: 5.0, 
      feedback: "Numeric answer provided, but AI verification is unavailable. Awarded base score of 5." 
    };
  }

  // 🎙️ STEP 3: Contextual Overlap (Did they use words from the question?)
  const qWords = lowerQue.split(/\s+/).filter(w => w.length > 4); 
  const overlap = qWords.filter(w => lowerAns.includes(w));
  const overlapScore = Math.min(2.0, (overlap.length * 0.5));
  baseScore += overlapScore;

  // 🎙️ STEP 4: Quality Scoring (Focused on depth and keywords over pure length)
  const foundKW = ANSWER_KEYWORDS.filter(k => lowerAns.includes(k));
  let kwScore = Math.min(3.0, (foundKW.length * 0.6)); // Quality signal: Keywords
  baseScore += kwScore;

  // Final capped score out of 10
  const finalScore = Math.min(10, Math.round(baseScore * 10) / 10);

  // 🎙️ STEP 5: Dynamic Feedback Generation
  if (lowerAns.includes("dont know") || lowerAns.includes("no idea") || lowerAns.includes("i don't know")) {
    return { score: 5.0, feedback: "Candidate explicitly stated they do not know the answer. Awarded base score of 5." };
  }

  let finalFeedback = "";
  if (finalScore < 6) {
    finalFeedback = "The answer identifies core concepts, but lacks professional depth. Mention specific experiences or tools to strengthen your response.";
  } else if (finalScore < 8.5) {
    finalFeedback = "Solid answer! You've used relevant terminology and addressed the question well.";
  } else {
    finalFeedback = "Exceptional response showing strong domain knowledge and professional communication skills.";
  }

  return { score: finalScore, feedback: finalFeedback };
}

// ─── Keyword fallback for resume ───
const RESUME_KEYWORDS = [
  // Tech
  "javascript","react","node","python","java","sql","html","css","typescript",
  "angular","vue","mongodb","firebase","aws","docker","kubernetes","git","agile",
  "scrum","rest","api","machine learning","c++","c#","flutter","graphql","redis",
  "postgresql","mysql","linux","ci/cd","devops","next.js","express","tensorflow",
  // Generic / Business
  "management","leadership","marketing","sales","operations","design","financial",
  "communication","strategy","project","analysis","planning","customer","support",
  "teamwork","training","research","development","hr","human resources", "business"
];
const SECTION_KW = {
  education: ["education","university","college","degree","bachelor","master","gpa", "school", "b.tech", "bsc", "b.com", "hsc", "ssc", "curriculum"],
  skills: ["skills","technical skills","technologies","proficiency","tools", "expertise", "competencies", "languages"],
  projects: ["projects","project","built","developed","created","implemented", "portfolio", "contribution"],
  experience: ["experience","work experience","internship","company","role","position", "employment", "professional", "career"],
  summary: ["summary", "objective", "profile", "about me", "professional summary"]
};

function keywordScoreResume(resumeText, jobDescription = "") {
  if (!resumeText?.trim() || resumeText.length < 5) {
    return { 
      resumeScore: 0, 
      resumeFeedback: "No content", 
      detectedSkills: [], 
      strengths: [], 
      weaknesses: ["No content found"],
      foundSections: [],
      wordCount: 0,
      lengthScore: 0,
      keywordScore: 0,
      sectionScore: 0
    };
  }
  const lower = resumeText.toLowerCase();
  const wordCount = resumeText.split(/\s+/).filter(Boolean).length;
  
  // 🎙️ STEP 1: Participation Points
  // Minimum score of 5.0 as per user request
  let baseScore = 5.0;

  // Calculate Section Score (Max 3)
  const foundSections = Object.keys(SECTION_KW).filter(s => SECTION_KW[s].some(k => lower.includes(k)));
  const sectionScore = Math.min(3, foundSections.length * 0.75);

  // Calculate Length Score (Max 3)
  const lengthScore = wordCount < 20 ? 0 : wordCount <= 100 ? 1 : wordCount <= 250 ? 2 : 3;

  // Calculate Keyword Score (Max 4) based strictly on Job Description
  let targetKeywords = RESUME_KEYWORDS;
  if (jobDescription && jobDescription.length > 20) {
    const stops = new Set(["the","and","is","for","with","a","an","to","in","of","on","as","at","by","or","this","that","are"]);
    const tokens = [...new Set(jobDescription.toLowerCase().match(/\b[a-z]{3,}\b/g) || [])];
    const jdTokens = tokens.filter(w => !stops.has(w));
    if (jdTokens.length > 5) targetKeywords = jdTokens;
  }
  
  const detectedSkills = targetKeywords.filter(k => lower.includes(k));
  let keywordScore = 0;
  if (targetKeywords === RESUME_KEYWORDS) {
    keywordScore = detectedSkills.length >= 8 ? 4 : detectedSkills.length >= 5 ? 3 : detectedSkills.length >= 2 ? 1 : 0;
  } else {
    const matchRatio = detectedSkills.length / targetKeywords.length;
    keywordScore = matchRatio >= 0.5 ? 4 : matchRatio >= 0.3 ? 3 : matchRatio >= 0.15 ? 2 : matchRatio > 0 ? 1 : 0;
  }

  // Strict Final Score Addition
  let score = baseScore + lengthScore + sectionScore + keywordScore;
  score = Math.min(10, Math.round(score * 10) / 10);
  
  const strengths = [];
  const weaknesses = [];
  if (keywordScore >= 3) strengths.push(`Strong keyword matches with Job Description (${detectedSkills.slice(0, 2).join(", ")})`);
  if (sectionScore >= 2) strengths.push("Professional sections structured correctly");
  if (lengthScore >= 2) strengths.push("Sufficient content length for professional review");
  
  if (lengthScore < 2) weaknesses.push("Resume content is short or missing key details");
  if (keywordScore < 2) weaknesses.push("Resume fails to naturally hit the required Job Description keywords");
  if (sectionScore < 2) weaknesses.push("Missing critical professional sections (Education, Experience, etc.)");

  // Create descriptive feedback based on scores
  let feedback = "";
  if (score >= 8.5) {
    feedback = "Excellent resume! It is well-structured, keyword-rich, and perfectly matches the target role requirements.";
  } else if (score >= 7) {
    feedback = "Strong resume with good section coverage and relevant keywords. A few more specific project details could push it to excellent.";
  } else if (score >= 5.5) {
    feedback = "Average resume. It meets basic requirements but lacks depth in either technical keywords or professional sections.";
  } else {
    feedback = "The resume needs significant improvement in structure, content length, and alignment with job-specific keywords.";
  }

  return {
    resumeScore: score,
    resumeFeedback: feedback,
    detectedSkills,
    strengths,
    weaknesses,
    foundSections,
    wordCount,
    lengthScore,
    keywordScore,
    sectionScore
  };
}

// ─── OpenAI helper ───
async function callOpenAI(messages, maxTokens = 300, isJson = false) {
  const proxyUrl = window.location.hostname === "localhost" ? "http://localhost:4000/api/ai" : "/api/ai";
  
  const body = {
    messages,
    max_tokens: maxTokens,
    temperature: 0.1,
  };

  if (isJson) {
    body.response_format = { type: "json_object" };
  }

  try {
    const response = await fetch(proxyUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${GEMINI_API_KEY}`,
      },
      body: JSON.stringify(body),
    });

    // Read the response body FIRST before checking status
    const data = await response.json();

    if (!response.ok) {
      // Log the EXACT error from OpenAI/proxy
      console.error("❌ AI Proxy returned error:", response.status, JSON.stringify(data));
      
      if (data.openai_error?.code === "insufficient_quota") {
        console.error("💰 Your OpenAI account has NO CREDITS. Go to https://platform.openai.com/settings/organization/billing to add funds.");
      } else if (response.status === 401) {
        console.error("🔑 API Key is INVALID or MISSING. Check Cloudflare env vars.");
      } else if (response.status === 429) {
        console.error("⏳ Rate limited by OpenAI. Too many requests.");
      }
      
      return null;
    }

    return data.choices[0]?.message?.content?.trim();
  } catch (err) {
    console.error("AI Call Failed (network error):", err);
    return null;
  }
}

// ─── Public: Evaluate interview answer ───
export async function evaluateAnswer(question, answer, jobDescription = "", category = "") {
  if (!answer?.trim()) return { score: 0, feedback: "No answer provided." };
  
  const isAptitude = category.toLowerCase().includes("aptitude") || category.toLowerCase().includes("math");

  const jdContext = jobDescription
    ? `\n\nThe candidate is applying for a position with the following job description:\n"""${jobDescription}"""\nEvaluate the answer considering how relevant it is to this role.`
    : "";

  const aptitudeInst = isAptitude
    ? "\n\nCRITICAL: This is an Aptitude/Math question. You must verify if the candidate's numeric answer is 100% CORRECT. If it is correct, rate it 10/10. If the answer is INCORRECT, rate it 5/10. Do not penalize for lack of conversational filler."
    : "";

  try {
    const raw = await callOpenAI([
      {
        role: "system",
        content: `You are an expert technical interviewer. Evaluate the candidate's answer.${jdContext}${aptitudeInst}
CRITICAL RULES:
1. MINIMUM SCORE: For any non-empty answer, the score MUST be at least 5/10.
2. MATH/APTITUDE: This is the highest priority. If the question involves math, calculations, or aptitude, you MUST verify the numeric result. 
   - If the answer is CORRECT: You MUST give exactly 10/10.
   - If the answer is INCORRECT: You MUST give exactly 5/10.
   - Do not give any other score for math questions.
3. QUALITY OVER QUANTITY: For non-math questions, evaluate based on accuracy and relevance. Do NOT reward length over quality.
Return ONLY a JSON object: {"score": <5 or 10 for math, 5-10 for others>, "feedback": "<short evaluation>"}.`,
      },
      {
        role: "user",
        content: `Question: ${question}\n\nAnswer: ${answer}`,
      },
    ], 1000, true);

    if (raw) {
      try {
        const jsonMatch = raw.match(/\{[\s\S]*\}/);
        if (!jsonMatch) throw new Error("No JSON object found in response");
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          score: Math.min(10, Math.max(5, Number(parsed.score))),
          feedback: parsed.feedback || "Evaluated by AI.",
        };
      } catch (parseErr) {
        console.error("AI Answer Parse Error:", parseErr, "Raw text:", raw);
      }
    }
  } catch (err) {
    console.warn("OpenAI answer eval failed, using fallback:", err.message);
  }

  return keywordScoreAnswer(question, answer, category);
}

// ─── Public: Analyze resume ───
export async function analyzeResume(resumeText, jobDescription = "") {
  if (!resumeText?.trim()) {
    return { resumeScore: 0, resumeFeedback: "Improve", detectedSkills: [], strengths: [], weaknesses: ["No resume content"] };
  }

  // Increased context for 2026 standards
  const truncated = resumeText.slice(0, 6000);

  const jdContext = jobDescription
    ? `\n\nThe candidate is applying for a position with the following job description:\n"""${jobDescription}"""\nRate the resume based on how well it matches this specific role. Highlight relevant and missing skills for this position.`
    : "";

  try {
    const raw = await callOpenAI(
      [
        {
          role: "system",
          content: `You are an expert HR recruiter. Analyze the resume text and return a JSON object:${jdContext}
CRITICAL RULE: The minimum score for any resume MUST be at least 5/10.
{
  "resumeScore": <5-10>,
  "resumeFeedback": "Good|Average|Improve",
  "detectedSkills": ["skill1",...],
  "strengths": ["strength1",...],
  "weaknesses": ["weakness1",...]
}`,
        },
        {
          role: "user",
          content: `Resume text:\n\n${truncated}`,
        },
      ],
      500,
      true
    );

    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        console.log("AI Resume Analysis Successful:", parsed.resumeScore);
        
        // Compute manual metrics to ensure they always exist for the frontend dashboard UI
        const wordCount = resumeText.split(/\s+/).filter(Boolean).length;
        const lower = resumeText.toLowerCase();
        const SECTION_KW = {
          education: ["education","university","college","degree","bachelor","master","gpa", "school", "b.tech", "bsc", "b.com"],
          skills: ["skills","technical skills","technologies","proficiency","tools", "expertise"],
          projects: ["projects","project","built","developed","created","implemented", "portfolio"],
          experience: ["experience","work experience","internship","company","role","position", "employment"],
        };
        const foundSections = Object.keys(SECTION_KW).filter(s => SECTION_KW[s].some(k => lower.includes(k)));
        const sectionScore = Math.min(3, foundSections.length * 0.75);
        const lengthScore = wordCount < 20 ? 0 : wordCount <= 100 ? 1 : wordCount <= 250 ? 2 : 3;
        
        let targetKeywords = RESUME_KEYWORDS;
        if (jobDescription && jobDescription.length > 20) {
          const stops = new Set(["the","and","is","for","with","a","an","to","in","of","on","as","at","by","or","this","that","are"]);
          const tokens = [...new Set(jobDescription.toLowerCase().match(/\b[a-z]{3,}\b/g) || [])];
          const jdTokens = tokens.filter(w => !stops.has(w));
          if (jdTokens.length > 5) targetKeywords = jdTokens;
        }
        const detectedSkillsArr = targetKeywords.filter(k => lower.includes(k));
        let keywordScore = 0;
        if (targetKeywords === RESUME_KEYWORDS) {
          keywordScore = detectedSkillsArr.length >= 8 ? 4 : detectedSkillsArr.length >= 5 ? 3 : detectedSkillsArr.length >= 2 ? 1 : 0;
        } else {
          const matchRatio = detectedSkillsArr.length / targetKeywords.length;
          keywordScore = matchRatio >= 0.5 ? 4 : matchRatio >= 0.3 ? 3 : matchRatio >= 0.15 ? 2 : matchRatio > 0 ? 1 : 0;
        }

        return {
          resumeScore: Math.min(10, Math.max(5, Number(parsed.resumeScore || 0))),
          resumeFeedback: parsed.resumeFeedback || "Average",
          detectedSkills: Array.isArray(parsed.detectedSkills) ? parsed.detectedSkills : [],
          strengths: Array.isArray(parsed.strengths) ? parsed.strengths : [],
          weaknesses: Array.isArray(parsed.weaknesses) ? parsed.weaknesses : [],
          foundSections,
          wordCount,
          lengthScore,
          sectionScore,
          keywordScore
        };
      } catch (parseErr) {
        console.error("AI Resume Parse Error:", parseErr, "Raw text:", raw);
      }
    }
  } catch (err) {
    console.warn("OpenAI resume analysis failed, using fallback:", err.message);
  }

  // Use Strict JD Fallback
  return keywordScoreResume(resumeText, jobDescription);
}

// ─── Public: Translate text to English ───
export async function translateToEnglish(text) {
  if (!text?.trim()) return "";
  
  try {
    const raw = await callOpenAI([
      {
        role: "system",
        content: `You are an expert translator. Translate the following text into clear, professional English. If the text is already in English, just return it as is. Do not add any conversational filler. Return a JSON object: {"translatedText": "<translated english string>"}.`
      },
      {
        role: "user",
        content: `Text to translate: ${text}`
      }
    ], 300, true);

    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        return parsed.translatedText || text;
      } catch (e) {
        console.error("AI Translation Parse Error:", e);
      }
    }
  } catch (err) {
    console.warn("OpenAI translation failed:", err.message);
  }
  
  // Fallback to original text if translation fails
  return text;
}

export { keywordScoreAnswer, keywordScoreResume };
